// src/components/enterprise/DataIntegrationPanel.jsx
//
// The slide-out behind the console's "Data Integration Layer" button. It shows
// the three legs every console figure stands on, and what this organization
// has on each one right now:
//   1. Member check-ins: the ERI, aggregated at k >= 5 (individual data, never shown)
//   2. The organization's own data: typed in the ROI tab or uploaded as CSV
//   3. Business averages: conservative published-style benchmarks
// Precedence for a business input is client figure > uploaded figure > average
// (docs/EXECUTIVE-METRICS.md), and every number in the console carries its
// source. Nothing here claims a connection that does not exist.

import React from 'react';
import { Database, X, Users, Building2, Scale, Check, Clock, FileSpreadsheet } from 'lucide-react';

const STATUS = {
    live:    ['Live',       'text-emerald-700 bg-emerald-50 border-emerald-100', Check],
    partial: ['Partial',    'text-amber-700 bg-amber-50 border-amber-100', Clock],
    none:    ['Not yet',    'text-slate-500 bg-slate-50 border-slate-200', Clock],
    builtin: ['Built in',   'text-sky-700 bg-sky-50 border-sky-100', Check]
};

const Badge = ({ status }) => {
    const [label, cls, Icon] = STATUS[status] || STATUS.none;
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded border ${cls}`}>
            <Icon size={13} /> {label}
        </span>
    );
};

const ICONS = { members: Users, organization: Building2, averages: Scale };

const DataIntegrationPanel = ({ isOpen, onClose, legs = [], canUpload = false, onUpload = null }) => {
    if (!isOpen) return null;
    return (
        <>
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-40 transition-opacity" onClick={onClose}></div>

            <div className="fixed inset-y-0 right-0 w-full md:w-[520px] bg-white shadow-[-10px_0_40px_rgba(0,0,0,0.1)] z-50 flex flex-col">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-[var(--brand-deep)] text-white">
                    <div>
                        <h2 className="text-xl font-black flex items-center gap-2">
                            <Database size={20} className="text-[var(--brand-yellow)]" /> What the numbers stand on
                        </h2>
                        <p className="text-xs text-white/70 mt-1 font-medium tracking-wide uppercase">Three sources, every figure labelled</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-5 bg-slate-50">
                    <p className="text-sm text-slate-600 leading-relaxed">
                        Every number in this console is built from the same three legs. A member's own answers are never shown;
                        groups under five are suppressed. For a business input, the organization's own figure wins over an uploaded
                        one, and an uploaded one wins over the average.
                    </p>

                    {legs.map((leg) => {
                        const Icon = ICONS[leg.id] || Database;
                        return (
                            <div key={leg.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                <div className="p-4 border-b border-slate-100 flex justify-between items-start gap-3 bg-slate-50/50">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[var(--brand-blue)] shrink-0">
                                            <Icon size={18} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-0.5">{leg.kicker}</p>
                                            <p className="font-bold text-[var(--brand-deep)] leading-tight">{leg.name}</p>
                                        </div>
                                    </div>
                                    <Badge status={leg.status} />
                                </div>
                                <div className="p-4 space-y-3">
                                    <p className="text-sm text-slate-700 leading-relaxed">{leg.summary}</p>
                                    {leg.rows && leg.rows.length > 0 && (
                                        <dl className="grid grid-cols-1 gap-1.5">
                                            {leg.rows.map((r) => (
                                                <div key={r.label} className="flex justify-between gap-4 text-xs">
                                                    <dt className="text-slate-500">{r.label}</dt>
                                                    <dd className="font-semibold text-slate-800 text-right tabular-nums">{r.value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                    )}
                                    {leg.note && <p className="text-[11px] text-slate-500 leading-relaxed">{leg.note}</p>}
                                    {leg.id === 'organization' && canUpload && (
                                        <button type="button" onClick={onUpload} className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg hover:border-[var(--brand-blue)] text-xs font-bold text-slate-700 transition-colors">
                                            <FileSpreadsheet size={16} /> Bring figures in: type one, upload a file, or request a system
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default DataIntegrationPanel;
